import { useCallback, useEffect, useMemo, useState } from 'react';
import { dateKeyInTZ } from '@onward-delivery/design/calendar-utils';
import { useClientUser } from '@/hooks';

import { msUntilNextBusinessDay } from './utils';

// Formats a calendar date, which is a UTC-pinned position rather than a moment — hence
// timeZone: 'UTC' and no tz argument.
export const useFormatters = () => {
    const dayFull = useMemo(() => {
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
        });
    }, []);

    return { dayFull };
};

// Today's date key in the business zone, kept live. Computed once at module load it went stale in a
// tab left open overnight: the past/future split, the lock controls and the day a Fleet save
// end-dates rules from all kept reading yesterday.
//
// A timer to the next business midnight, plus a check whenever the tab comes back — background tabs
// throttle timers, so the timer alone can fire late or not at all while the laptop sleeps.
export const useTodayKey = (tz) => {
    const [todayKey, setTodayKey] = useState(() => dateKeyInTZ(new Date().toISOString(), tz));

    useEffect(() => {
        let timer;

        const refresh = () => {
            clearTimeout(timer);
            const now = new Date();
            // Same string, same state: React skips the render when nothing rolled over.
            setTodayKey(dateKeyInTZ(now.toISOString(), tz));
            // A second past midnight, so the timer cannot fire a hair early and re-read yesterday.
            timer = setTimeout(refresh, msUntilNextBusinessDay(now, tz) + 1000);
        };

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                refresh();
            }
        };

        refresh();
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', refresh);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', refresh);
        };
    }, [tz]);

    return todayKey;
};

export const useResourceCallbacks = (callbacks) => {
    const user = useClientUser();

    const upsertResources = useCallback(({ resource, rules, overrides, regions, shipping_partners }) => {
        return callbacks.upsertResources({
            variables: {
                resources: {
                    ...resource,
                    client_id: user?.user_id,
                    subregions: {
                        data:
                            (regions.add || []).map(({ subregion_id }) => ({
                                subregion_id,
                            })) || [],
                    },
                    overrides: {
                        data: overrides.add || [],
                    },
                    rules: {
                        data: rules.add || [],
                    },
                    shipping_partners: {
                        data: shipping_partners.add || [],
                        on_conflict: {
                            constraint: 'resource_shipping_partner_const',
                            update_columns: ['enabled'],
                        },
                    },
                },
                regions: regions.update || [],
                region_removals: (regions.remove || []).map(({ mapping_id }) => mapping_id),
                rules: rules.update || [],
                rule_removals: (rules.remove || []).map(({ mapping_id }) => mapping_id),
                overrides: overrides.update || [],
                override_removals: (overrides.remove || []).map(({ mapping_id }) => mapping_id),
                shipping_partners: shipping_partners?.update || [],
                globals: (shipping_partners?.globals || []).map((set) => ({
                    _set: {
                        enabled: set.enabled,
                    },
                    where: {
                        _and: [
                            {
                                resource_id: { _eq: set.resource_id },
                            },
                            {
                                partner_client_id: { _is_null: true },
                            },
                        ],
                    },
                })),
            },
        });
    }, []);

    // The side dialog's Save: one day's worth of lock changes, already collapsed by
    // collectSchedulingWrites. One mutation carrying both halves, so Hasura applies them in a single
    // transaction and a mid-way failure cannot leave a half-applied Lock All behind.
    //
    // Resolves with `{ errors }` rather than rejecting on failure — the mutation has an onError —
    // so the caller has to read the result to know whether to drop its staged toggles.
    const setSchedulingOverrides = useCallback(({ overrides = [], deletes = [] } = {}) => {
        return callbacks.setSchedulingLocks({ variables: { overrides, deletes } });
    }, []);

    const deleteResource = useCallback(({ resource }) => {
        return callbacks.deleteResource({
            variables: {
                resource: resource.resource_id,
            },
        });
    }, []);

    const editResource = useCallback(([resource, rule, day]) => {
        callbacks.editResource({
            resource,
            rule,
            day,
        });
    }, []);

    // Memoised, not a fresh literal: the store spreads this into the Context value, and a new
    // object every render defeats React.memo on the day cells no matter what else is stable.
    return useMemo(
        () => ({ upsertResources, editResource, deleteResource, setSchedulingOverrides }),
        [upsertResources, editResource, deleteResource, setSchedulingOverrides]
    );
};
